import React from 'react';
import _ from 'lodash';
import Icon from 'components/Icon';
import { getCookie } from 'helpers/Storage';
import {
  isAMobileDevice,
  isATabletDevice,
  largeTabletView as isLargeScreenTablet,
} from 'helpers/BreakpointHelper';
import { appDownloadPromptEnabled } from 'helpers/ConfigHelper';
import { msToDate, now, differenceInMilliseconds } from 'helpers/DateTimeHelper';
import { getPreviewPassTime } from 'helpers/AuthHelper';
import headerTheme from './Header.v1.module.scss';

const SMARTBANNER_CLOSED = 'smartbanner-closed';
const SMARTBANNER_INSTALLED = 'smartbanner-installed';
const WATCH = 'watch';
const LIVE = 'live';

const { afSub2, afPrt, referral, afChannel, afSub1, afWebDPBase, isRetargeting, app } = _.get(
  window.ExternalConfig,
  'appDownload',
  {},
);

const DEFAULT_DEVICE = {
  afSub2,
  afPrt,
  referral,
  afChannel,
  afSub1,
  afWebDPBase,
  isRetargeting,
  app,
  c: '',
};

const IOS = {
  ...DEFAULT_DEVICE,
  c: _.get(window.ExternalConfig, 'appDownload.c.ios'),
};
const CHROME_OPERA = {
  ...DEFAULT_DEVICE,
  c: _.get(window.ExternalConfig, 'appDownload.c.chrome_opera'),
};
const ANDROID = {
  ...DEFAULT_DEVICE,
  c: _.get(window.ExternalConfig, 'appDownload.c.android'),
};
const ANDROID_VOD = {
  ...DEFAULT_DEVICE,
  c: _.get(window.ExternalConfig, 'appDownload.c.android_vod'),
};
const LIVE_IOS_ANDROID = {
  ...DEFAULT_DEVICE,
  c: _.get(window.ExternalConfig, 'appDownload.c.live_ios_android'),
};

const APP_DOWNLOAD_CONFIG = _.get(window.ExternalConfig, 'appDownload', {});

const getAppUrlForDevice = ({ device, url, campaignOverrides }) => {
  const calcDevice = {
    ...device,
    ...campaignOverrides,
  };
  const {
    afSub2,
    afPrt,
    referral,
    afChannel,
    c,
    afSub1,
    afWebDPBase,
    isRetargeting,
    app,
  } = calcDevice;
  const afWebDp = `${afWebDPBase}cmpid=org=${afSub2}::mc=${referral}::src=${afChannel}::cmp=${c}::add=${afSub1}`;
  const afDp = `${app}://home?cmpid=org=${afSub2}::mc=${referral}::src=${afChannel}::cmp=${c}::add=${afSub1}&af_web_dp=${afWebDp}`;
  return `${url}?af_sub2=${afSub2}&af_prt=${afPrt}&pid=${referral}&af_channel=${afChannel}&c=${c}&af_sub1=${afSub1}&is_retargeting=${isRetargeting}&af_dp=${afDp}`;
};

export const getAppUrlList = ({ campaignOverrides }) => {
  const { promoUrl: url } = APP_DOWNLOAD_CONFIG;
  return {
    android: {
      default: getAppUrlForDevice({ device: ANDROID, url, campaignOverrides }),
      chrome: getAppUrlForDevice({ device: CHROME_OPERA, url, campaignOverrides }),
      opera: getAppUrlForDevice({ device: CHROME_OPERA, url, campaignOverrides }),
      watch: getAppUrlForDevice({ device: ANDROID_VOD, url, campaignOverrides }),
      live: getAppUrlForDevice({ device: LIVE_IOS_ANDROID, url, campaignOverrides }),
    },
    kindle: {
      default: getAppUrlForDevice({ device: ANDROID, url, campaignOverrides }),
      chrome: getAppUrlForDevice({ device: CHROME_OPERA, url, campaignOverrides }),
      opera: getAppUrlForDevice({ device: CHROME_OPERA, url, campaignOverrides }),
    },
    ios: {
      default: getAppUrlForDevice({ device: IOS, url, campaignOverrides }),
      chrome: getAppUrlForDevice({ device: CHROME_OPERA, url, campaignOverrides }),
      opera: getAppUrlForDevice({ device: CHROME_OPERA, url, campaignOverrides }),
      live: getAppUrlForDevice({ device: LIVE_IOS_ANDROID, url, campaignOverrides }),
    },
    default: getAppUrlForDevice({ device: DEFAULT_DEVICE, url, campaignOverrides }),
  };
};

const determineDeviceType = ({ campaignOverrides }) => {
  const UA = navigator.userAgent;
  const appUrlList = getAppUrlList({ campaignOverrides });
  let type;

  if (UA.match(/Windows Phone 8/i) != null && UA.match(/Touch/i) !== null) {
    type = 'windows';
  } else if (UA.match(/iPhone|iPod/i) != null || (UA.match(/iPad/) && _.get(appUrlList, 'ios'))) {
    type = 'ios'; // Check webview and native smart banner support (iOS 6+)
  } else if (
    UA.match(/\bSilk\/(.*\bMobile Safari\b)?/) ||
    UA.match(/\bKF\w/) ||
    UA.match('Kindle Fire')
  ) {
    type = 'kindle';
  } else if (UA.match(/Android/i) != null) {
    type = 'android';
  }
  return type;
};

export const determineBrowser = () => {
  const UA = navigator.userAgent;
  let browser;
  if (!UA) return browser;
  // Detect browser to use the right tracking URL
  if (UA.match(/CriOS/i) != null || UA.match(/Chrome/i)) {
    if (UA.match(/(Version\/)\d(.)\d/) || UA.match(/(SamsungBrowser\/)\d(.)\d/)) {
      browser = null;
    } else {
      browser = 'chrome';
    }
  } else if (UA.match(/OPiOS/i) || UA.match(/Opera Mini/i)) {
    browser = 'opera';
  } else if (UA.match(/Safari/i)) {
    browser = 'safari';
  }
  return browser;
};

export const getAppUrl = ({ isLive, isWatch, campaignOverrides }) => {
  let page;
  if (isLive) page = LIVE;
  if (isWatch) page = WATCH;
  const type = determineDeviceType({ campaignOverrides });
  const browser = page || determineBrowser();
  const fallbackDevice = _.get(getAppUrlList({ campaignOverrides }), 'default');
  const deviceUrlList = _.get(getAppUrlList({ campaignOverrides }), type, fallbackDevice);
  const fallbackBrowser = _.get(deviceUrlList, 'default');
  const browserUrl = _.get(deviceUrlList, browser, fallbackBrowser);
  return browserUrl;
};

const smartBannerIcon = <Icon color="white" icon="phone" viewBox="0 4 16 18" />;

const divider = <div className={headerTheme.divider} />;

export const smartBannerSettings = ({ isLive, isWatch, campaignOverrides, onClose, onInstall }) => {
  const { title, author } = APP_DOWNLOAD_CONFIG;
  return {
    title,
    author,
    price: {
      ios: 'FREE',
      android: 'FREE',
      windows: 'FREE',
      kindle: 'FREE',
    },
    storeText: {
      ios: 'On the App Store',
      android: 'In Google Play',
      windows: 'In Windows store',
      kindle: 'In Amazon Appstore',
    },
    button: [smartBannerIcon, ' Open in the App', divider],
    url: getAppUrl({ isLive, isWatch, campaignOverrides }),
    daysHidden: 1,
    daysReminder: 1,
    force: '',
    ignoreIosVersion: true,
    appMeta: {
      ios: 'apple-itunes-app-pseudo',
      android: 'google-play-app',
      windows: 'msApplication-ID',
      kindle: 'kindle-fire-app',
    },
    onClose,
    onInstall,
  };
};

export const appIconUrl = ({ campaignOverrides }) => {
  const deviceType = determineDeviceType({ campaignOverrides });
  const { iosImage: iosImageUrl, androidImage: androidImageUrl } = APP_DOWNLOAD_CONFIG;
  if (deviceType === 'android') return androidImageUrl;
  return iosImageUrl;
};

export const smartBannerPresent = breakpoint => {
  if (!(isAMobileDevice() || isATabletDevice())) {
    return isLargeScreenTablet(breakpoint);
  }
  const smartBannerClosedCookie = getCookie({ name: SMARTBANNER_CLOSED });
  const smartBannerInstalledCookie = getCookie({ name: SMARTBANNER_INSTALLED });
  return appDownloadPromptEnabled() && !smartBannerClosedCookie && !smartBannerInstalledCookie;
};

export const getPreviewPassTimeDiff = (previewPassTime = null) => {
  const ppTime = previewPassTime || getPreviewPassTime();
  const ppDate = ppTime ? msToDate(ppTime) : null;
  const timeDiff = ppDate ? differenceInMilliseconds(ppDate, now()) : null;

  return timeDiff;
};
